/**
 * Tabelas de referência — FIPE, Fraga e o estudo de mercado.
 *
 * As três são consulta pura sobre bases que vêm de fora, então não há trava nem
 * aviso de dado global aqui: nada nesta tela grava.
 *
 * **Paginam no servidor.** São 6.900, 22.192 e 14.534 registros; trazer qualquer
 * uma inteira para o navegador não ajudaria ninguém a achar nada.
 */

import { useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { ErroDaApi } from "@/api/erros"
import { catalogoPorProjeto } from "@/api/query"
import { usarAssuntos, usarSistemasDeConteudo } from "@/api/catalogosDeConteudo"
import { Faixa } from "@/kit/Faixa"
import { api } from "@/api/cliente"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { CONSULTAS, consultaDe, type ColunaDaConsulta, type Consulta } from "./consultas"

type Linha = Record<string, unknown>
type Escolhas = Record<string, string[]>

export function TabelasDeReferencia() {
  // As três dividem esta tela e cada uma tem seu caminho no menu (/fipe, /fraga,
  // /estudo-mercado). O caminho decide qual abre; daí em diante o seletor manda.
  const { pathname } = useLocation()
  const [chave, setChave] = useState(() => {
    const doCaminho = pathname.replace(/^\//, "")
    return CONSULTAS.some((c) => c.chave === doCaminho) ? doCaminho : CONSULTAS[0]!.chave
  })
  const consulta = consultaDe(chave)

  const [escolhas, setEscolhas] = useState<Escolhas>({})
  const [porId, setPorId] = useState<Record<string, number>>({})
  const [pagina, setPagina] = useState(1)
  const [aberta, setAberta] = useState<Linha | null>(null)

  function trocar(nova: string) {
    setChave(nova)
    setEscolhas({})
    setPorId({})
    setPagina(1)
    setAberta(null)
  }

  const params = useMemo(() => {
    const p: Record<string, unknown> = { pagina: pagina, pageSize: consulta.porPagina }
    for (const [k, v] of Object.entries(escolhas)) if (v.length) p[k] = v
    for (const [k, v] of Object.entries(porId)) if (v > 0) p[k] = v
    return p
  }, [escolhas, porId, pagina, consulta.porPagina])

  const dados = useQuery({
    queryKey: ["referencia", chave, params] as const,
    queryFn: () => obter<{
      itens?: Linha[]
      totalRegistros?: number
      totalItens?: number
    }>(consulta.rota, params),
  })

  const itens = dados.data?.itens ?? []
  const total = dados.data?.totalRegistros ?? dados.data?.totalItens ?? 0
  const paginas = Math.max(1, Math.ceil(total / consulta.porPagina))
  const quantosFiltros =
    Object.values(escolhas).reduce((n, v) => n + v.length, 0) +
    Object.values(porId).filter((v) => v > 0).length

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo={consulta.nome}
        detalhe={dados.data ? `${total.toLocaleString("pt-BR")} registros` : undefined}
      >
        <div style={{ display: "flex", gap: 2, background: "var(--surface2)", padding: 2, borderRadius: 6 }}>
          {CONSULTAS.map((c) => (
            <button
              key={c.chave}
              onClick={() => trocar(c.chave)}
              style={{
                border: 0, borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                background: chave === c.chave ? "var(--surface)" : "transparent",
                color: chave === c.chave ? "var(--txt)" : "var(--txt2)",
                fontWeight: chave === c.chave ? 600 : 400,
              }}
            >
              {c.nome}
            </button>
          ))}
        </div>

        {consulta.rotaDeExportacao && (
          <Botao
            aoClicar={() => { void baixar(consulta.rotaDeExportacao!, params, consulta.chave) }}
          >
            Exportar
          </Botao>
        )}
      </Faixa>

      <div style={{ padding: "8px 16px", borderBottom: "1px solid var(--line2)" }}>
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt2)", lineHeight: 1.5, maxWidth: "88ch" }}>
          {consulta.oQueE}
        </p>
        {consulta.ressalva && (
          <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5, maxWidth: "88ch" }}>
            {consulta.ressalva}
          </p>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{
          flex: "0 0 250px", width: 250, overflowY: "auto", padding: 12,
          borderRight: "1px solid var(--line2)",
        }}>
          <PorId consulta={consulta} valores={porId}
                 aoMudar={(k, v) => { setPorId((x) => ({ ...x, [k]: v })); setPagina(1) }} />

          <Filtros
            consulta={consulta}
            escolhas={escolhas}
            aoMudar={(k, v) => { setEscolhas((x) => ({ ...x, [k]: v })); setPagina(1) }}
          />

          {quantosFiltros > 0 && (
            <button
              onClick={() => { setEscolhas({}); setPorId({}); setPagina(1) }}
              style={{
                marginTop: 8, border: 0, background: "transparent", color: "var(--acao)",
                cursor: "pointer", fontSize: 11.5, padding: "4px 0",
              }}
            >
              limpar os {quantosFiltros} filtros
            </button>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {dados.isPending ? (
              <p style={{ padding: 14, fontSize: 12.5, color: "var(--txt2)" }}>Consultando…</p>
            ) : dados.error ? (
              <p style={{ padding: 14, fontSize: 12.5, color: "var(--erro)" }}>
                {dados.error instanceof ErroDaApi ? dados.error.message : "Não foi possível consultar."}
              </p>
            ) : itens.length === 0 ? (
              <p style={{ padding: 14, fontSize: 12.5, color: "var(--txt3)" }}>
                Nada com esses filtros.
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "var(--surface)", zIndex: 1 }}>
                    {consulta.colunas.map((c) => (
                      <th key={c.chave} style={{
                        textAlign: c.numero ? "right" : "left", padding: "6px 8px",
                        fontSize: 10.5, fontWeight: 600, color: "var(--txt2)",
                        borderBottom: "1px solid var(--line)", whiteSpace: "nowrap", width: c.largura,
                      }}>
                        {c.rotulo}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {itens.map((l, i) => (
                    <tr
                      key={String(l.id ?? i)}
                      onClick={() => consulta.detalheCompleto && setAberta(l)}
                      style={{
                        borderBottom: "1px solid var(--line2)",
                        cursor: consulta.detalheCompleto ? "pointer" : "default",
                        background: aberta?.id === l.id ? "var(--acao-soft)" : undefined,
                      }}
                    >
                      {consulta.colunas.map((c) => (
                        <td key={c.chave} style={{
                          padding: "4px 8px",
                          textAlign: c.numero ? "right" : "left",
                          fontVariantNumeric: c.numero ? "tabular-nums" : undefined,
                          maxWidth: c.largura ?? 260,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }} title={String(l[c.chave] ?? "")}>
                          <Celula valor={l[c.chave]} coluna={c} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {paginas > 1 && (
            <div style={{
              flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8,
              padding: "7px 14px", borderTop: "1px solid var(--line2)",
            }}>
              <Botao aoClicar={() => setPagina((p) => Math.max(1, p - 1))} desligado={pagina <= 1}>
                anterior
              </Botao>
              <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>
                {pagina} de {paginas.toLocaleString("pt-BR")}
              </span>
              <Botao aoClicar={() => setPagina((p) => Math.min(paginas, p + 1))} desligado={pagina >= paginas}>
                próxima
              </Botao>
            </div>
          )}
        </div>

        {aberta && <Detalhe linha={aberta} aoFechar={() => setAberta(null)} />}
      </div>

      <BarraDeEstado direita={`${consulta.porPagina} por página · ${consulta.medido.toLocaleString("pt-BR")} na medição`}>
        {consulta.detalheCompleto && !aberta ? "Clique numa linha para ver o registro inteiro." : ""}
      </BarraDeEstado>
    </div>
  )
}

function Celula({ valor, coluna }: { valor: unknown; coluna: ColunaDaConsulta }) {
  if (coluna.marcador) {
    const sim = Boolean(valor)
    return (
      <span style={{
        fontSize: 10.5, padding: "1px 7px", borderRadius: 3,
        background: sim ? "var(--acao-soft)" : "var(--surface2)",
        color: sim ? "var(--acao-on-soft)" : "var(--txt3)",
      }}>
        {sim ? "sim" : "não"}
      </span>
    )
  }
  if (valor === null || valor === undefined || valor === "") return <>—</>
  if (typeof valor === "number" && coluna.numero) {
    return <>{coluna.ano ? String(valor) : valor.toLocaleString("pt-BR")}</>
  }
  return <>{String(valor)}</>
}

// ---------------------------------------------------------------- filtros

function PorId({ consulta, valores, aoMudar }: {
  consulta: Consulta
  valores: Record<string, number>
  aoMudar: (chave: string, valor: number) => void
}) {
  const assuntos = usarAssuntos()
  const sistemas = usarSistemasDeConteudo()

  if (!consulta.filtrosPorId?.length) return null

  return (
    <>
      {consulta.filtrosPorId.map((f) => {
        const itens = f.catalogo === "assuntos" ? assuntos.itens : sistemas.itens
        return (
          <div key={f.chave} style={{ marginBottom: 9 }}>
            <label style={rot}>{f.rotulo}</label>
            <select
              value={valores[f.chave] ?? 0}
              onChange={(e) => aoMudar(f.chave, Number(e.target.value))}
              style={campo}
            >
              <option value={0}>Todos</option>
              {itens.slice(0, 400).map((x) => (
                <option key={x.id} value={x.id}>{x.rotulo}</option>
              ))}
            </select>
          </div>
        )
      })}
    </>
  )
}

function Filtros({ consulta, escolhas, aoMudar }: {
  consulta: Consulta
  escolhas: Escolhas
  aoMudar: (chave: string, valores: string[]) => void
}) {
  /**
   * A Fraga entrega as opções de todos os campos numa chamada só; a FIPE, uma
   * por campo, já reduzida pelo que foi escolhido antes — cascata de verdade,
   * que ali existe na API.
   */
  const deUmaVez = useQuery({
    queryKey: ["referencia-filtros", consulta.chave] as const,
    enabled: consulta.filtros.some((f) => f.origem === "deUmaVez") && Boolean(consulta.rotaDeFiltros),
    ...catalogoPorProjeto,
    queryFn: () => obter<Record<string, string[]>>(consulta.rotaDeFiltros!, {}),
  })

  return (
    <>
      {consulta.filtros.map((f) => (
        <FiltroDeUmCampo
          key={f.chave}
          consulta={consulta}
          chave={f.chave}
          rotulo={f.rotulo}
          origem={f.origem}
          opcoesProntas={deUmaVez.data?.[f.chave]}
          escolhidos={escolhas[f.chave] ?? []}
          jaEscolhido={escolhas}
          aoMudar={(v) => aoMudar(f.chave, v)}
        />
      ))}
    </>
  )
}

function FiltroDeUmCampo({ consulta, chave, rotulo, origem, opcoesProntas, escolhidos, jaEscolhido, aoMudar }: {
  consulta: Consulta
  chave: string
  rotulo: string
  origem: "porColuna" | "deUmaVez"
  opcoesProntas?: string[]
  escolhidos: string[]
  jaEscolhido: Escolhas
  aoMudar: (v: string[]) => void
}) {
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState("")

  // Só busca as opções quando a pessoa abre a lista: são sete campos, e sete
  // chamadas ao abrir a tela seriam seis a mais do que ela vai usar.
  const porColuna = useQuery({
    queryKey: ["referencia-filtro", consulta.chave, chave, jaEscolhido] as const,
    enabled: aberto && origem === "porColuna" && Boolean(consulta.rotaDeFiltros),
    ...catalogoPorProjeto,
    queryFn: () => obter<{ coluna: string; valores: string[] }>(
      `${consulta.rotaDeFiltros}/${chave}`, jaEscolhido),
  })

  const todas = origem === "deUmaVez" ? (opcoesProntas ?? []) : (porColuna.data?.valores ?? [])

  const achadas = useMemo(() => {
    const t = busca.trim().toLowerCase()
    const base = t ? todas.filter((x) => x.toLowerCase().includes(t)) : todas
    return base.slice(0, 200)
  }, [todas, busca])

  function alternar(v: string) {
    aoMudar(escolhidos.includes(v) ? escolhidos.filter((x) => x !== v) : [...escolhidos, v])
  }

  return (
    <div style={{ marginBottom: 9 }}>
      <label style={{ ...rot, display: "flex", gap: 6 }}>
        {rotulo}
        {escolhidos.length > 0 && <span style={{ color: "var(--acao)" }}>{escolhidos.length}</span>}
      </label>

      {escolhidos.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 3 }}>
          {escolhidos.map((v) => (
            <span key={v} style={{
              display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10,
              padding: "1px 3px 1px 6px", borderRadius: 3, background: "var(--surface2)",
              maxWidth: 210,
            }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
              <button onClick={() => alternar(v)} aria-label={`Tirar ${v}`} style={{
                border: 0, background: "transparent", cursor: "pointer",
                color: "var(--txt3)", fontSize: 12, lineHeight: 1, padding: "0 2px",
              }}>×</button>
            </span>
          ))}
        </div>
      )}

      <button onClick={() => setAberto((a) => !a)} style={{
        width: "100%", textAlign: "left", padding: "4px 7px", fontSize: 11,
        border: "1px solid var(--line)", borderRadius: 5, cursor: "pointer",
        background: "var(--surface)", color: "var(--txt2)",
      }}>
        {aberto ? "fechar" : escolhidos.length ? "mais…" : "escolher…"}
      </button>

      {aberto && (
        <div style={{ marginTop: 3, border: "1px solid var(--line2)", borderRadius: 5, padding: 4 }}>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="filtrar…"
            autoFocus
            style={{ ...campo, fontSize: 10.5, marginBottom: 3, padding: "3px 6px" }}
          />
          <div style={{ maxHeight: 150, overflowY: "auto" }}>
            {porColuna.isFetching ? (
              <p style={{ fontSize: 10.5, color: "var(--txt3)", padding: 3 }}>procurando…</p>
            ) : achadas.length === 0 ? (
              <p style={{ fontSize: 10.5, color: "var(--txt3)", padding: 3 }}>nada aqui</p>
            ) : (
              achadas.map((v) => (
                <label key={v} style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "1px 3px",
                  fontSize: 11, cursor: "pointer",
                }}>
                  <input type="checkbox" checked={escolhidos.includes(v)} onChange={() => alternar(v)} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
                </label>
              ))
            )}
          </div>
          {todas.length > achadas.length && (
            <p style={{ margin: "2px 0 0", fontSize: 10, color: "var(--txt3)" }}>
              {todas.length.toLocaleString("pt-BR")} opções; mostrando {achadas.length}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- detalhe

/**
 * O registro inteiro. Serve para a Fraga, onde cada linha tem 129 campos e a
 * tabela mostra sete: o resto não some, só sai da frente.
 */
function Detalhe({ linha, aoFechar }: { linha: Linha; aoFechar: () => void }) {
  const [busca, setBusca] = useState("")

  const campos = useMemo(() => {
    const todos = Object.entries(linha).filter(([, v]) => v !== null && v !== "" && v !== undefined)
    const t = busca.trim().toLowerCase()
    if (!t) return todos
    return todos.filter(([k, v]) =>
      k.toLowerCase().includes(t) || String(v).toLowerCase().includes(t))
  }, [linha, busca])

  const vazios = Object.keys(linha).length - Object.entries(linha)
    .filter(([, v]) => v !== null && v !== "" && v !== undefined).length

  return (
    <aside style={{
      flex: "0 0 340px", width: 340, overflowY: "auto",
      borderLeft: "1px solid var(--line2)", background: "var(--surface)", padding: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <b style={{ fontSize: 12.5 }}>Registro completo</b>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      <input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="procurar campo…"
        style={{ ...campo, marginBottom: 8 }}
      />

      <p style={{ margin: "0 0 8px", fontSize: 10.5, color: "var(--txt3)" }}>
        {campos.length} campos preenchidos{vazios > 0 && `; ${vazios} em branco não aparecem`}
      </p>

      {campos.map(([k, v]) => (
        <div key={k} style={{
          display: "flex", gap: 8, padding: "3px 0", borderBottom: "1px solid var(--line2)",
        }}>
          <span style={{ flex: "0 0 44%", fontSize: 10.5, color: "var(--txt3)", wordBreak: "break-word" }}>
            {humano(k)}
          </span>
          <span style={{ flex: 1, fontSize: 11, wordBreak: "break-word" }}>
            {typeof v === "boolean" ? (v ? "sim" : "não") : String(v)}
          </span>
        </div>
      ))}
    </aside>
  )
}

/** `potencia_CV` → "Potencia CV". Os nomes vêm do arquivo da Fraga, crus. */
function humano(chave: string): string {
  const s = chave.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2")
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function Botao({ children, aoClicar, desligado }: {
  children: React.ReactNode
  aoClicar: () => void
  desligado?: boolean
}) {
  return (
    <button onClick={aoClicar} disabled={desligado} style={{
      border: "1px solid var(--line)", borderRadius: 6, padding: "4px 10px", fontSize: 12,
      cursor: desligado ? "default" : "pointer",
      background: "var(--surface)", color: desligado ? "var(--txt3)" : "var(--txt)",
    }}>
      {children}
    </button>
  )
}

const rot: React.CSSProperties = {
  display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3,
}
const campo: React.CSSProperties = {
  width: "100%", padding: "4px 7px", fontSize: 11.5,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}


/**
 * Baixa a exportação da consulta.
 *
 * Pelo cliente axios, e não por um `<a href>`: o token vive em memória e não em
 * cookie, então um link sairia sem autenticação. Leva os **filtros**, não a
 * página — quem exporta quer o recorte, não as cinquenta linhas na tela.
 */
async function baixar(rota: string, params: Record<string, unknown>, chave: string) {
  // Sem paginacao: as tres grafias aparecem, porque as consultas vieram de
  // controllers diferentes e cada um batizou a sua.
  const semPaginacao = { ...params }
  for (const k of ["pagina", "page", "qtdPorPagina", "pageSize"]) delete semPaginacao[k]

  const r = await api.get(rota, { params: semPaginacao, responseType: "blob" })

  const disp = String(r.headers?.["content-disposition"] ?? "")
  const achado = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp)
  const nome = achado ? decodeURIComponent(achado[1]!) : `${chave}.xlsx`

  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
